/**
 * =============================================================================
 * ROASTBREW - PROFILE / SETTINGS PAGE
 * =============================================================================
 *
 * Standalone settings page (no sidebar) matching ManageSubscription pattern.
 *
 * SECTIONS:
 * 1. Account & Profile — avatar, display name, email, change password
 * 2. Subscription Management — Stripe portal link, plan details, payment
 * 3. Privacy & Security — 2FA toggle, active sessions
 * 4. Notifications — push notifications, email marketing
 *
 * =============================================================================
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from './AuthContext';
import { TIER_COLORS, isLightTheme as tierIsLight, tierRank } from './config';
import stripePromise from './stripe';
import UserAvatar from './UserAvatar';


// =============================================================================
// ADD CARD FORM — must be a child of <Elements> to use Stripe hooks
// =============================================================================

function AddCardForm({ clientSecret, onSuccess, onCancel, themeColors }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');
    try {
      const cardNumber = elements.getElement(CardNumberElement);
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        { payment_method: { card: cardNumber } }
      );
      if (stripeError) {
        setError(stripeError.message);
      } else if (setupIntent?.payment_method) {
        onSuccess(typeof setupIntent.payment_method === 'string' ? setupIntent.payment_method : setupIntent.payment_method.id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const elementStyle = {
    base: {
      fontSize: '15px', fontFamily: '"DM Sans", -apple-system, sans-serif',
      color: themeColors.text, letterSpacing: '0.02em',
      '::placeholder': { color: themeColors.muted + '80' },
      iconColor: themeColors.accent,
    },
    invalid: { color: '#E24B4A', iconColor: '#E24B4A' },
  };

  const fieldStyle = {
    padding: '14px 16px', borderRadius: '10px',
    background: themeColors.text === '#F5F5F0' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
    border: `1px solid ${themeColors.muted}25`, transition: 'border-color 0.2s ease',
  };

  return (
    <div>
      <p style={{ fontSize: '13px', fontWeight: '500', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Card Details
      </p>
      <div style={{ ...fieldStyle, marginBottom: '8px' }}>
        <CardNumberElement options={{ style: elementStyle, showIcon: true }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <div style={fieldStyle}><CardExpiryElement options={{ style: elementStyle }} /></div>
        <div style={fieldStyle}><CardCvcElement options={{ style: elementStyle }} /></div>
      </div>
      {error && <p style={{ fontSize: '13px', color: '#E24B4A', fontFamily: '"DM Sans", sans-serif', marginTop: '8px' }}>{error}</p>}
      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
        {onCancel && (
          <button onClick={onCancel} disabled={processing}
            style={{ flex: 1, padding: '13px', fontSize: '13px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif', letterSpacing: '0.04em', background: 'transparent', border: `1px solid ${themeColors.muted}30`, borderRadius: '8px', color: themeColors.muted, cursor: 'pointer', transition: 'all 0.2s ease' }}>
            Cancel
          </button>
        )}
        <button onClick={handleSubmit} disabled={processing || !stripe}
          style={{ flex: 1, padding: '13px', fontSize: '13px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif', letterSpacing: '0.04em', background: themeColors.accent, border: 'none', borderRadius: '8px', color: '#1A1A1A', cursor: processing ? 'wait' : 'pointer', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: processing ? 0.7 : 1 }}>
          {processing ? (<><span style={{ width: '14px', height: '14px', border: '2px solid transparent', borderTopColor: 'currentColor', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />Saving...</>) : (onCancel ? 'Add Card' : 'Update Card')}
        </button>
      </div>
    </div>
  );
}


export default function ProfilePage() {
  const navigate = useNavigate();
  const { profile, logout, API_URL, api, refreshAccount, syncSubscription } = useAuth();
  const user = profile ? { name: profile.name, email: profile.email } : null;
  const currentTier = profile?.currentTier || 'free';

  // ---------------------------------------------------------------------------
  // THEME
  // ---------------------------------------------------------------------------

  const themeColors = TIER_COLORS[currentTier] || TIER_COLORS.free;
  const isLightTheme = tierIsLight(currentTier);
  const borderColor = isLightTheme ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)';
  const cardBg = isLightTheme ? '#FFFFFF' : 'rgba(255,255,255,0.03)';
  const subtleBg = isLightTheme ? 'rgba(26,26,26,0.04)' : 'rgba(255,255,255,0.04)';

  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------

  const displayName = user?.name || 'User';
  const email = user?.email || '';

  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState(displayName);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);

  // TODO: Re-enable when Privacy & Security / Notifications sections are wired up
  // const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  // const [showSessions, setShowSessions] = useState(false);
  // const [pushNotifications, setPushNotifications] = useState(true);
  // const [emailMarketing, setEmailMarketing] = useState(false);

  const [saveToast, setSaveToast] = useState(false);

  // Payment method modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState(null);
  const [paymentMethodError, setPaymentMethodError] = useState('');

  // Invoice history modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  // TODO: Re-enable when Privacy & Security section is wired up
  // const activeSessions = [ ... ];
  // const handleRevokeSession = (sessionId) => { ... };

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setProfileError('');
    try {
      const res = await api.fetch('/accounts/me', {
        method: 'PUT',
        body: JSON.stringify({ displayName: editName.trim() }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save');
      }
      setIsEditingProfile(false);
      await refreshAccount();
      setSaveToast(true);
      setTimeout(() => setSaveToast(false), 3000);
    } catch (err) {
      setProfileError(err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordResetLoading(true);
    try {
      const res = await api.fetch('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error('Failed to send reset link');
      setPasswordResetSent(true);
      setTimeout(() => setPasswordResetSent(false), 5000);
    } catch (err) {
      console.error('Password reset request failed:', err);
    } finally {
      setPasswordResetLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // PAYMENT METHOD HANDLERS
  // ---------------------------------------------------------------------------

  const fetchPaymentMethods = useCallback(async () => {
    setPaymentMethodsLoading(true);
    setPaymentMethodError('');
    try {
      const res = await api.fetch('/billing/payment-methods');
      if (!res.ok) throw new Error('Failed to load payment methods');
      const data = await res.json();
      setPaymentMethods(data.paymentMethods || []);
    } catch (err) {
      setPaymentMethodError(err.message);
    } finally {
      setPaymentMethodsLoading(false);
    }
  }, [api]);

  const openPaymentModal = async () => {
    setShowPaymentModal(true);
    setShowAddCard(false);
    setSetupClientSecret(null);
    fetchPaymentMethods();
  };

  const startAddCard = async () => {
    setPaymentMethodError('');
    try {
      const res = await api.fetch('/billing/create-setup-intent', { method: 'POST' });
      if (!res.ok) throw new Error('Failed to initialize card form');
      const data = await res.json();
      setSetupClientSecret(data.clientSecret);
      setShowAddCard(true);
    } catch (err) {
      setPaymentMethodError(err.message);
    }
  };

  const handleCardAdded = async (paymentMethodId) => {
    setPaymentMethodError('');
    try {
      const res = await api.fetch('/billing/set-default-payment-method', {
        method: 'POST',
        body: JSON.stringify({ paymentMethodId }),
      });
      if (!res.ok) {
        // 402: the card was saved, but retrying the failed payment with it was declined.
        const data = await res.json().catch(() => ({}));
        if (res.status === 402) {
          setShowAddCard(false);
          setSetupClientSecret(null);
          fetchPaymentMethods();
        }
        throw new Error(data.error || 'Failed to set default payment method');
      }
      setShowAddCard(false);
      setSetupClientSecret(null);
      fetchPaymentMethods();
      await syncSubscription(); // a retried payment unlocks the app straight away
    } catch (err) {
      setPaymentMethodError(err.message);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    setPaymentMethodError('');
    try {
      const res = await api.fetch('/billing/set-default-payment-method', {
        method: 'POST',
        body: JSON.stringify({ paymentMethodId }),
      });
      if (!res.ok) {
        // 402: the card was made the default, but retrying the failed payment with it was declined.
        const data = await res.json().catch(() => ({}));
        if (res.status === 402) fetchPaymentMethods();
        throw new Error(data.error || 'Failed to set default');
      }
      fetchPaymentMethods();
      await syncSubscription(); // a retried payment unlocks the app straight away
    } catch (err) {
      setPaymentMethodError(err.message);
    }
  };

  const handleRemoveCard = async (paymentMethodId) => {
    setPaymentMethodError('');
    try {
      const res = await api.fetch(`/billing/payment-method/${paymentMethodId}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to remove card');
      }
      fetchPaymentMethods();
    } catch (err) {
      setPaymentMethodError(err.message);
    }
  };

  // ---------------------------------------------------------------------------
  // INVOICE HISTORY HANDLERS
  // ---------------------------------------------------------------------------

  const openInvoiceModal = async () => {
    setShowInvoiceModal(true);
    setInvoicesLoading(true);
    try {
      const res = await api.fetch('/billing/invoices');
      if (!res.ok) throw new Error('Failed to load invoices');
      const data = await res.json();
      setInvoices(data.invoices || []);
    } catch (err) {
      console.error('Failed to load invoices:', err);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const formatInvoiceDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatAmount = (amountInCents, currency) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'usd' }).format(amountInCents / 100);
  };

  // ---------------------------------------------------------------------------
  // SUB-COMPONENTS
  // ---------------------------------------------------------------------------

  const SectionHeader = ({ icon, title, subtitle }) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '14px',
      marginBottom: '24px', paddingBottom: '20px',
      borderBottom: `1px solid ${borderColor}`,
    }}>
      <div style={{
        width: '40px', height: '40px', borderRadius: '10px',
        background: `${themeColors.accent}15`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </div>
      <div>
        <h2 style={{
          fontSize: '18px', fontWeight: '500', color: themeColors.text,
          fontFamily: '"Cormorant Garamond", serif', marginBottom: '2px',
        }}>
          {title}
        </h2>
        <p style={{
          fontSize: '13px', color: themeColors.muted,
          fontFamily: '"DM Sans", sans-serif',
        }}>
          {subtitle}
        </p>
      </div>
    </div>
  );

  const SettingRow = ({ label, description, children, noBorder }) => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: '16px', padding: '16px 0',
      borderBottom: noBorder ? 'none' : `1px solid ${borderColor}`,
      flexWrap: 'wrap',
    }}>
      <div style={{ flex: 1, minWidth: '200px' }}>
        <p style={{
          fontSize: '14px', fontWeight: '500', color: themeColors.text,
          fontFamily: '"DM Sans", sans-serif', marginBottom: '2px',
        }}>
          {label}
        </p>
        {description && (
          <p style={{
            fontSize: '13px', color: themeColors.muted,
            fontFamily: '"DM Sans", sans-serif',
          }}>
            {description}
          </p>
        )}
      </div>
      <div style={{ flexShrink: 0 }}>
        {children}
      </div>
    </div>
  );

  const Button = ({ onClick, children, variant = 'default', disabled = false }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '8px 16px',
        fontSize: '13px',
        fontWeight: '500',
        fontFamily: '"DM Sans", sans-serif',
        background: variant === 'danger'
          ? 'rgba(220, 60, 60, 0.08)'
          : subtleBg,
        color: variant === 'danger'
          ? '#B33A3A'
          : themeColors.text,
        border: `1px solid ${variant === 'danger' ? 'rgba(220, 60, 60, 0.2)' : borderColor}`,
        borderRadius: '8px',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.borderColor = variant === 'danger' ? '#B33A3A' : themeColors.accent;
      }}
      onMouseLeave={(e) => {
        if (!disabled) e.currentTarget.style.borderColor = variant === 'danger' ? 'rgba(220, 60, 60, 0.2)' : borderColor;
      }}
    >
      {children}
    </button>
  );

  // TODO: Re-enable when Privacy & Security / Notifications sections are wired up
  // const Toggle = ({ enabled, onChange }) => ( ... );

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <div style={{
      minHeight: '100vh',
      background: themeColors.bg,
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      transition: 'background 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Ambient Background */}
      <div style={{
        position: 'fixed', inset: 0,
        background: isLightTheme
          ? 'radial-gradient(ellipse at 30% 0%, rgba(201, 169, 98, 0.06) 0%, transparent 50%), radial-gradient(ellipse at 70% 100%, rgba(201, 169, 98, 0.04) 0%, transparent 50%)'
          : 'radial-gradient(ellipse at 50% 0%, rgba(201, 169, 98, 0.1) 0%, transparent 40%)',
        pointerEvents: 'none',
      }} />

      {/* Header */}
      <header style={{
        padding: '24px 48px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'relative', zIndex: 10,
      }}>
        <button
          onClick={() => navigate('/')}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            background: 'none', border: 'none', color: themeColors.muted,
            fontSize: '14px', fontFamily: '"DM Sans", sans-serif',
            cursor: 'pointer', padding: '8px 0', transition: 'color 0.2s ease',
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = themeColors.text}
          onMouseLeave={(e) => e.currentTarget.style.color = themeColors.muted}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to Home
        </button>
      </header>

      {/* Main Content */}
      <main style={{
        maxWidth: '720px', margin: '0 auto',
        padding: '20px 24px 80px',
        position: 'relative', zIndex: 1,
      }}>
        {/* Page Title */}
        <div style={{
          textAlign: 'center', marginBottom: '48px',
          animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          <h1 style={{
            fontSize: 'clamp(36px, 5vw, 48px)', fontWeight: '300',
            color: themeColors.text, marginBottom: '12px', letterSpacing: '-0.02em',
          }}>
            Settings
          </h1>
          <p style={{
            fontSize: '16px', color: themeColors.muted,
            fontFamily: '"DM Sans", sans-serif', lineHeight: 1.6,
          }}>
            Manage your account, privacy, and preferences
          </p>
        </div>

        {/* ================================================================
            ACCOUNT & PROFILE
            ================================================================ */}
        <section style={{
          background: cardBg, borderRadius: '16px',
          border: `1px solid ${borderColor}`, padding: '32px', marginBottom: '24px',
          animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.05s both',
        }}>
          <SectionHeader
            icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={themeColors.accent} strokeWidth="1.5">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="7" r="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            title="Account & Profile"
            subtitle="Manage your personal information"
          />

          {/* Avatar & Info */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '20px',
            padding: '20px 0', borderBottom: `1px solid ${borderColor}`,
          }}>
            {/* Initial avatar from avatars.js. Uses the real name, not the
                'User' placeholder, so a missing name shows the RoastBrew
                fallback rather than "U". */}
            <UserAvatar
              name={user?.name}
              size={80}
              style={{
                boxShadow: isLightTheme
                  ? '0 4px 12px rgba(0,0,0,0.18)'
                  : `0 0 0 1px ${themeColors.accent}55, 0 8px 24px ${themeColors.accent}30`,
              }}
            />
            <div style={{ flex: 1 }}>
              <p style={{
                fontSize: '18px', fontWeight: '500', color: themeColors.text,
                fontFamily: '"DM Sans", sans-serif', marginBottom: '4px',
              }}>
                {displayName}
              </p>
              <p style={{
                fontSize: '14px', color: themeColors.muted,
                fontFamily: '"DM Sans", sans-serif',
              }}>
                {email}
              </p>
            </div>
            <Button onClick={() => setIsEditingProfile(!isEditingProfile)}>
              {isEditingProfile ? 'Cancel' : 'Edit'}
            </Button>
          </div>

          {/* Edit Form */}
          {isEditingProfile && (
            <div style={{
              padding: '20px 0', borderBottom: `1px solid ${borderColor}`,
              animation: 'fadeIn 0.3s ease',
            }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block', fontSize: '12px', fontWeight: '500',
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  color: themeColors.muted, marginBottom: '8px',
                  fontFamily: '"DM Sans", sans-serif',
                }}>
                  Display Name
                </label>
                <input
                  type="text" value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  style={{
                    width: '100%', padding: '12px 14px', fontSize: '14px',
                    border: `1px solid ${borderColor}`, borderRadius: '8px',
                    background: isLightTheme ? '#FFFFFF' : 'rgba(255,255,255,0.05)',
                    color: themeColors.text, outline: 'none',
                    fontFamily: '"DM Sans", sans-serif', boxSizing: 'border-box',
                    transition: 'border-color 0.3s ease',
                  }}
                  onFocus={(e) => e.target.style.borderColor = themeColors.accent}
                  onBlur={(e) => e.target.style.borderColor = borderColor}
                />
              </div>
              {profileError && (
                <p style={{ fontSize: '13px', color: '#E24B4A', fontFamily: '"DM Sans", sans-serif', marginBottom: '12px' }}>{profileError}</p>
              )}
              <Button onClick={handleSaveProfile} disabled={savingProfile || !editName.trim()}>
                {savingProfile ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}

          <SettingRow label="Password" description={passwordResetSent ? 'Reset link sent to your email!' : 'Change your account password'} noBorder>
            <Button onClick={handleChangePassword} disabled={passwordResetLoading || passwordResetSent}>
              {passwordResetSent ? 'Link Sent ✓' : passwordResetLoading ? 'Sending...' : 'Change Password'}
            </Button>
          </SettingRow>
        </section>

        {/* ================================================================
            SUBSCRIPTION MANAGEMENT
            ================================================================ */}
        <section style={{
          background: cardBg, borderRadius: '16px',
          border: `1px solid ${borderColor}`, padding: '32px', marginBottom: '24px',
          animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both',
        }}>
          <SectionHeader
            icon={
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={themeColors.accent} strokeWidth="1.5">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M1 10h22" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            title="Subscription & Billing"
            subtitle="Manage your plan and payment details"
          />

          <SettingRow
            label="Current Plan"
            description={`You're on the ${themeColors.label} plan`}
          >
            <button
              onClick={() => navigate('/subscription')}
              style={{
                padding: '8px 16px', fontSize: '13px', fontWeight: '500',
                fontFamily: '"DM Sans", sans-serif',
                background: `${themeColors.accent}15`,
                color: themeColors.accent,
                border: `1px solid ${themeColors.accent}40`,
                borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = themeColors.accent;
                e.currentTarget.style.color = isLightTheme ? '#F5F5F0' : '#1A1A1A';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `${themeColors.accent}15`;
                e.currentTarget.style.color = themeColors.accent;
              }}
            >
              Manage Plan
            </button>
          </SettingRow>

          <SettingRow
            label="Payment Method"
            description="Manage your cards and billing info"
          >
            <Button
              onClick={openPaymentModal}
              disabled={tierRank(currentTier) === 0}
            >
              {tierRank(currentTier) === 0 ? 'No active plan' : 'Update Payment'}
            </Button>
          </SettingRow>

          <SettingRow
            label="Billing History"
            description="View past invoices and receipts"
            noBorder
          >
            <Button
              onClick={openInvoiceModal}
              disabled={tierRank(currentTier) === 0}
            >
              {tierRank(currentTier) === 0 ? 'No history' : 'View History'}
            </Button>
          </SettingRow>
        </section>


        {/* ================================================================
            TODO: Enable these sections when backend is wired up
            - Privacy & Security: 2FA, active sessions, revoke
            - Notifications: push notifications, email marketing
            ================================================================ */}

        {/* Sign Out */}
        <div style={{
          textAlign: 'center', paddingTop: '16px',
          animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.25s both',
        }}>
          <button
            onClick={logout}
            style={{
              padding: '12px 32px', fontSize: '14px', fontWeight: '500',
              fontFamily: '"DM Sans", sans-serif', letterSpacing: '0.06em',
              textTransform: 'uppercase',
              background: 'transparent',
              color: '#B33A3A',
              border: '1px solid rgba(220, 60, 60, 0.2)',
              borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(220, 60, 60, 0.08)';
              e.currentTarget.style.borderColor = '#B33A3A';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'rgba(220, 60, 60, 0.2)';
            }}
          >
            Sign Out
          </button>
        </div>
      </main>

      {/* =================================================================
          PAYMENT METHOD MODAL
          ================================================================= */}
      {showPaymentModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{
            background: isLightTheme ? '#FFFFFF' : '#1A1A1A', borderRadius: '20px',
            maxWidth: '480px', width: '90%', animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: `1px solid ${isLightTheme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}`,
            maxHeight: '90vh', overflow: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
          }}>
            <div style={{ padding: '28px 32px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: '300', color: themeColors.text, fontFamily: '"Cormorant Garamond", Georgia, serif' }}>Payment Methods</h3>
                <p style={{ fontSize: '13px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif', marginTop: '4px' }}>Manage your cards on file</p>
              </div>
              <button onClick={() => { setShowPaymentModal(false); setShowAddCard(false); setSetupClientSecret(null); setPaymentMethodError(''); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: themeColors.muted, transition: 'color 0.2s ease' }}
                onMouseEnter={(e) => e.currentTarget.style.color = themeColors.text}
                onMouseLeave={(e) => e.currentTarget.style.color = themeColors.muted}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div style={{ margin: '20px 32px 0', height: '1px', background: `linear-gradient(90deg, transparent, ${themeColors.muted}20, transparent)` }} />
            <div style={{ padding: '24px 32px 32px' }}>
              {paymentMethodError && (
                <div style={{ fontSize: '13px', color: '#E24B4A', fontFamily: '"DM Sans", sans-serif', padding: '10px 14px', borderRadius: '8px', background: 'rgba(226, 75, 74, 0.08)', marginBottom: '20px' }}>
                  {paymentMethodError}
                </div>
              )}
              {paymentMethodsLoading ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <div style={{ width: '24px', height: '24px', border: '2px solid rgba(128,128,128,0.15)', borderTopColor: themeColors.accent, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
                  <p style={{ fontSize: '13px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif' }}>Loading cards...</p>
                </div>
              ) : (
                <div style={{ marginBottom: '24px' }}>
                  {paymentMethods.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '28px 0' }}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={themeColors.muted + '40'} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                      </svg>
                      <p style={{ fontSize: '14px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif' }}>No payment methods on file</p>
                    </div>
                  ) : paymentMethods.map((pm, idx) => (
                    <div key={pm.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '16px 18px', borderRadius: '12px', marginBottom: idx < paymentMethods.length - 1 ? '8px' : 0,
                      background: pm.isDefault ? (isLightTheme ? 'rgba(201,169,98,0.06)' : 'rgba(201,169,98,0.08)') : (isLightTheme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.04)'),
                      border: `1px solid ${pm.isDefault ? themeColors.accent + '30' : 'transparent'}`, transition: 'all 0.2s ease',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                        <div style={{ width: '40px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isLightTheme ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.08)', border: `1px solid ${themeColors.muted}15`, flexShrink: 0 }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={pm.isDefault ? themeColors.accent : themeColors.muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '500', color: themeColors.text, fontFamily: '"DM Sans", sans-serif', textTransform: 'capitalize' }}>{pm.brand}</span>
                            <span style={{ fontSize: '14px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif', letterSpacing: '0.05em' }}>····{pm.last4}</span>
                            {pm.isDefault && (
                              <span style={{ fontSize: '10px', fontWeight: '600', color: themeColors.accent, fontFamily: '"DM Sans", sans-serif', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '2px 8px', borderRadius: '10px', background: themeColors.accent + '15' }}>Default</span>
                            )}
                          </div>
                          <span style={{ fontSize: '12px', color: themeColors.muted + 'A0', fontFamily: '"DM Sans", sans-serif', marginTop: '2px', display: 'block' }}>Expires {pm.expMonth}/{pm.expYear}</span>
                        </div>
                      </div>
                      {!pm.isDefault && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                          <button onClick={() => handleSetDefault(pm.id)}
                            style={{ fontSize: '12px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif', color: themeColors.accent, background: 'none', border: 'none', cursor: 'pointer', padding: '6px 10px', borderRadius: '6px', transition: 'background 0.2s ease' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = themeColors.accent + '15'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                          >Set Default</button>
                          <button onClick={() => handleRemoveCard(pm.id)}
                            style={{ fontSize: '12px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif', color: themeColors.muted + '80', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 10px', borderRadius: '6px', transition: 'all 0.2s ease' }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#E24B4A'; e.currentTarget.style.background = 'rgba(226,75,74,0.08)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = themeColors.muted + '80'; e.currentTarget.style.background = 'none'; }}
                          >Remove</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {showAddCard && setupClientSecret ? (
                <div style={{ padding: '24px', borderRadius: '14px', background: isLightTheme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.06)', border: `1px solid ${themeColors.muted}15` }}>
                  <Elements stripe={stripePromise} options={{ appearance: { theme: isLightTheme ? 'stripe' : 'night' } }}>
                    <AddCardForm clientSecret={setupClientSecret} onSuccess={handleCardAdded} onCancel={() => { setShowAddCard(false); setSetupClientSecret(null); }} themeColors={themeColors} />
                  </Elements>
                </div>
              ) : (
                <button onClick={startAddCard}
                  style={{ width: '100%', padding: '15px', fontSize: '13px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif', letterSpacing: '0.04em', background: 'transparent', color: themeColors.muted, border: `1px dashed ${themeColors.muted}25`, borderRadius: '12px', cursor: 'pointer', transition: 'all 0.25s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = themeColors.accent + '60'; e.currentTarget.style.color = themeColors.accent; e.currentTarget.style.background = themeColors.accent + '06'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = themeColors.muted + '25'; e.currentTarget.style.color = themeColors.muted; e.currentTarget.style.background = 'transparent'; }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add New Card
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* =================================================================
          INVOICE HISTORY MODAL
          ================================================================= */}
      {showInvoiceModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{
            background: isLightTheme ? '#FFFFFF' : '#1A1A1A', borderRadius: '20px',
            maxWidth: '520px', width: '90%', animation: 'scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: `1px solid ${isLightTheme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}`,
            maxHeight: '90vh', overflow: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
          }}>
            <div style={{ padding: '28px 32px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: '300', color: themeColors.text, fontFamily: '"Cormorant Garamond", Georgia, serif' }}>Billing History</h3>
                <p style={{ fontSize: '13px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif', marginTop: '4px' }}>Past invoices and receipts</p>
              </div>
              <button onClick={() => setShowInvoiceModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: themeColors.muted, transition: 'color 0.2s ease' }}
                onMouseEnter={(e) => e.currentTarget.style.color = themeColors.text}
                onMouseLeave={(e) => e.currentTarget.style.color = themeColors.muted}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div style={{ margin: '20px 32px 0', height: '1px', background: `linear-gradient(90deg, transparent, ${themeColors.muted}20, transparent)` }} />
            <div style={{ padding: '24px 32px 32px' }}>
              {invoicesLoading ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <div style={{ width: '24px', height: '24px', border: '2px solid rgba(128,128,128,0.15)', borderTopColor: themeColors.accent, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
                  <p style={{ fontSize: '13px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif' }}>Loading invoices...</p>
                </div>
              ) : invoices.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '28px 0' }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={themeColors.muted + '40'} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px' }}>
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  <p style={{ fontSize: '14px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif' }}>No invoices yet</p>
                </div>
              ) : (
                <div>
                  {invoices.map((inv, idx) => (
                    <div key={idx} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '14px 16px', borderRadius: '10px', marginBottom: idx < invoices.length - 1 ? '6px' : 0,
                      background: isLightTheme ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.04)',
                      transition: 'background 0.2s ease',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isLightTheme ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)', flexShrink: 0 }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={themeColors.muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                          </svg>
                        </div>
                        <div>
                          <p style={{ fontSize: '14px', fontWeight: '500', color: themeColors.text, fontFamily: '"DM Sans", sans-serif' }}>
                            {formatAmount(inv.amountPaid, inv.currency)}
                          </p>
                          <p style={{ fontSize: '12px', color: themeColors.muted, fontFamily: '"DM Sans", sans-serif', marginTop: '1px' }}>
                            {formatInvoiceDate(inv.date)}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                        <span style={{
                          fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em',
                          padding: '3px 10px', borderRadius: '10px', fontFamily: '"DM Sans", sans-serif',
                          background: inv.status === 'paid' ? 'rgba(26, 127, 55, 0.1)' : 'rgba(226, 75, 74, 0.1)',
                          color: inv.status === 'paid' ? '#1a7f37' : '#E24B4A',
                        }}>
                          {inv.status}
                        </span>
                        {inv.hostedUrl && (
                          <a href={inv.hostedUrl} target="_blank" rel="noopener noreferrer"
                            style={{
                              fontSize: '12px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif',
                              color: themeColors.accent, textDecoration: 'none', padding: '4px 8px', borderRadius: '6px',
                              transition: 'background 0.2s ease', display: 'flex', alignItems: 'center', gap: '4px',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = themeColors.accent + '15'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            Receipt
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                            </svg>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Save Toast */}
      {saveToast && (
        <div style={{
          position: 'fixed', bottom: '32px', left: '50%', transform: 'translateX(-50%)',
          padding: '14px 24px', borderRadius: '12px',
          background: isLightTheme ? '#1A1A1A' : '#FFFFFF',
          color: isLightTheme ? '#FFFFFF' : '#1A1A1A',
          fontSize: '14px', fontWeight: '500', fontFamily: '"DM Sans", sans-serif',
          display: 'flex', alignItems: 'center', gap: '10px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          animation: 'fadeInUp 0.3s ease', zIndex: 101,
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Changes saved successfully
        </div>
      )}

      {/* Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,400&family=DM+Sans:wght@400;500;600&display=swap');
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; margin: 0; }
        input:focus { outline: none; }
      `}</style>
    </div>
  );
}
